import type { AdminTicket } from "@sd/types/admin";
import type {
  PaginatedResponse,
  TenantUser,
} from "@/utils/types/tenantUser";
import { looksLikeOpaqueUserId } from "@sd/lib/ticketRowMapping";

/** Unwrap common API list envelopes (grid_data, results, …). */
export function extractPayloadArray(payload: unknown): unknown[] {
  if (!payload) {
    return [];
  }
  if (Array.isArray(payload)) {
    return payload;
  }
  if (typeof payload !== "object") {
    return [];
  }

  const keys = [
    "grid_data",
    "results",
    "data",
    "tickets",
    "assets",
    "items",
    "records",
    "rows",
    "list",
    "content",
    "employees",
    "employee_list",
    "agents",
    "users",
    "members",
  ];

  const fromRecord = (o: Record<string, unknown>): unknown[] | null => {
    for (const k of keys) {
      const v = o[k];
      if (Array.isArray(v)) {
        return v;
      }
    }
    return null;
  };

  const o = payload as Record<string, unknown>;
  const flat = fromRecord(o);
  if (flat) {
    return flat;
  }

  for (const nest of ["data", "payload", "response", "result"]) {
    const inner = o[nest];
    if (Array.isArray(inner)) {
      return inner;
    }
    if (inner && typeof inner === "object") {
      const innerObj = inner as Record<string, unknown>;
      const nested = fromRecord(innerObj);
      if (nested) {
        return nested;
      }
    }
  }

  return [];
}

export function firstNonEmptyString(...vals: unknown[]): string | undefined {
  for (const v of vals) {
    if (v == null) {
      continue;
    }
    const s = String(v).trim();
    if (s) {
      return s;
    }
  }
  return undefined;
}

/** Case / variant-tolerant map lookup for ids from grid + APIs */
export function getUserDisplayFromMap(
  map: Map<string, string>,
  id: string
): string | undefined {
  const t = id.trim();
  if (!t) {
    return undefined;
  }
  return (
    map.get(t) ?? map.get(t.toLowerCase()) ?? map.get(t.toUpperCase())
  );
}

/**
 * `/service_desk/agent_employee_list/` and `/service_desk/employee_list/` rows:
 * firstname + lastname (and snake_case / fallbacks); `id` + `user_id` → display name.
 */
function mergeEmployeeLikeListIntoMap(
  map: Map<string, string>,
  payload: unknown
): void {
  for (const raw of extractPayloadArray(payload)) {
    if (!raw || typeof raw !== "object") {
      continue;
    }
    const o = raw as Record<string, unknown>;
    const id = String(o.id ?? o.pk ?? o.employee_id ?? o.agent_id ?? "").trim();
    const userId = String(o.user_id ?? o.django_user_id ?? "").trim();
    const employeeId = String(o.employee_id ?? o.agent_id ?? "").trim();
    const fullName = [
      o.firstname ?? o.first_name,
      o.lastname ?? o.last_name,
    ]
      .filter(Boolean)
      .map(String)
      .join(" ")
      .trim();
    const name = firstNonEmptyString(
      fullName || undefined,
      o.name,
      o.full_name,
      o.display_name,
      o.fullname,
      o.email,
      o.username
    );
    if (!name) {
      continue;
    }
    if (id && !map.has(id)) {
      map.set(id, name);
    }
    if (userId && userId !== id && !map.has(userId)) {
      map.set(userId, name);
    }
    if (employeeId && employeeId !== id && employeeId !== userId && !map.has(employeeId)) {
      map.set(employeeId, name);
    }
  }
}

/** ServiceDesk Enduser `grid_data`: `fullname` (+ fallbacks); common id keys. */
function mergeEnduserGridRowsIntoMap(
  map: Map<string, string>,
  enduserPayload: unknown
): void {
  const addKey = (set: Set<string>, v: unknown) => {
    const s = String(v ?? "").trim();
    if (s) {
      set.add(s);
    }
  };

  for (const raw of extractPayloadArray(enduserPayload)) {
    if (!raw || typeof raw !== "object") {
      continue;
    }
    const r = raw as Record<string, unknown>;
    let displayName =
      firstNonEmptyString(
        r.fullname,
        r.full_name,
        r.name,
        r.display_name,
        r.contact_name,
        r.customer_name,
        r.submitter_name
      )?.trim() ?? "";
    if (!displayName) {
      const email = String(
        r.email ?? r.emailaddress ?? r.contact_email ?? ""
      ).trim();
      if (email.includes("@")) {
        displayName = email.split("@")[0].replace(/[._]+/g, " ").trim();
      }
    }
    if (!displayName) {
      continue;
    }
    const keysToSet = new Set<string>();
    addKey(keysToSet, r.id);
    addKey(keysToSet, r.pk);
    addKey(keysToSet, r.uuid);
    addKey(keysToSet, r.user_id);
    addKey(keysToSet, r.user);
    addKey(keysToSet, r.auth_user);
    addKey(keysToSet, r.auth_user_id);
    addKey(keysToSet, r.enduser_id);
    addKey(keysToSet, r.django_user);
    addKey(keysToSet, r.django_user_id);
    addKey(keysToSet, r.account_id);
    addKey(keysToSet, r.portal_user_id);
    addKey(keysToSet, r.created_by);
    addKey(keysToSet, r.createdBy);
    addKey(keysToSet, r.tenant_user_id);
    addKey(keysToSet, r.tenant_user);
    for (const k of keysToSet) {
      if (!map.has(k)) {
        map.set(k, displayName);
      }
    }
  }
}

/**
 * ServiceDesk Customer Company `grid_data`: company / contact labels; ids and common FK keys.
 * Merged last so employee/enduser person names still win when the same key exists.
 */
function mergeCustomerCompanyGridRowsIntoMap(
  map: Map<string, string>,
  customerCompanyPayload: unknown
): void {
  const addKey = (set: Set<string>, v: unknown) => {
    const s = String(v ?? "").trim();
    if (s) {
      set.add(s);
    }
  };

  /** Skip objects — Formily FKs may be `{ id }` blobs, not string ids. */
  const addScalarKey = (set: Set<string>, v: unknown) => {
    if (v === undefined || v === null || typeof v === "object") {
      return;
    }
    const s = String(v).trim();
    if (s) {
      set.add(s);
    }
  };

  const addIdFromNested = (set: Set<string>, v: unknown) => {
    if (v && typeof v === "object" && !Array.isArray(v) && "id" in v) {
      addKey(set, (v as { id: unknown }).id);
    }
  };

  for (const raw of extractPayloadArray(customerCompanyPayload)) {
    if (!raw || typeof raw !== "object") {
      continue;
    }
    const r = raw as Record<string, unknown>;
    const fullName = [
      r.firstname ?? r.first_name,
      r.lastname ?? r.last_name,
    ]
      .filter(Boolean)
      .map(String)
      .join(" ")
      .trim();
    let displayName =
      firstNonEmptyString(
        r.company_name,
        r.legal_name,
        r.trade_name,
        r.trading_name,
        r.name,
        r.display_name,
        r.short_name,
        r.brand_name,
        r.contact_name,
        r.primary_contact_name,
        r.organization_name,
        r.org_name,
        fullName || undefined,
        r.fullname,
        r.full_name,
        r.title
      )?.trim() ?? "";
    if (!displayName) {
      const email = String(
        r.email ?? r.contact_email ?? r.primary_email ?? r.company_email ?? ""
      ).trim();
      if (email.includes("@")) {
        displayName = email.split("@")[0].replace(/[._]+/g, " ").trim();
      }
    }
    if (!displayName) {
      continue;
    }
    const keysToSet = new Set<string>();
    addKey(keysToSet, r.id);
    addKey(keysToSet, r.pk);
    addKey(keysToSet, r.uuid);
    addKey(keysToSet, r.user_id);
    addKey(keysToSet, r.company_id);
    addScalarKey(keysToSet, r.customer_company_id);
    addScalarKey(keysToSet, r.customer_company);
    addIdFromNested(keysToSet, r.customer_company);
    addScalarKey(keysToSet, r.service_desk_customer_company_id);
    addScalarKey(keysToSet, r.service_desk_customer_company);
    addIdFromNested(keysToSet, r.service_desk_customer_company);
    addScalarKey(keysToSet, r.primary_contact_id);
    addScalarKey(keysToSet, r.primary_contact);
    addIdFromNested(keysToSet, r.primary_contact);
    addKey(keysToSet, r.admin_id);
    addKey(keysToSet, r.tenant_id);
    for (const k of [
      "customer_company",
      "company",
      "service_desk_customer_company",
    ] as const) {
      addIdFromNested(keysToSet, r[k]);
    }
    for (const k of keysToSet) {
      if (!map.has(k)) {
        map.set(k, displayName);
      }
    }
  }
}

/**
 * `Map<uuid → name>` from: `agent_employee_list`, `employee_list`, ServiceDesk Enduser
 * grid_data, ServiceDesk Customer Company grid_data.
 */
export function buildCombinedRequesterDisplayMap(
  agentEmployeePayload: unknown,
  employeeListPayload: unknown,
  enduserPayload: unknown,
  customerCompanyPayload: unknown
): Map<string, string> {
  const m = new Map<string, string>();
  mergeEmployeeLikeListIntoMap(m, agentEmployeePayload);
  mergeEmployeeLikeListIntoMap(m, employeeListPayload);
  mergeEnduserGridRowsIntoMap(m, enduserPayload);
  mergeCustomerCompanyGridRowsIntoMap(m, customerCompanyPayload);
  return m;
}

export function buildUserDisplayMap(
  agentListPayload: unknown,
  tenantUsers: PaginatedResponse<TenantUser> | undefined | null
): Map<string, string> {
  const m = new Map<string, string>();
  for (const raw of extractPayloadArray(agentListPayload)) {
    if (!raw || typeof raw !== "object") {
      continue;
    }
    const o = raw as Record<string, unknown>;
    const id = String(o.id ?? "").trim();
    const userId = String(o.user_id ?? "").trim();
    // Support both first_name (snake) and firstname (no separator)
    const fullName = [o.first_name ?? o.firstname, o.last_name ?? o.lastname]
      .filter(Boolean)
      .map(String)
      .join(" ")
      .trim();
    const name = firstNonEmptyString(
      fullName || undefined,
      o.name,
      o.full_name,
      o.display_name,
      o.email,
      o.username
    );
    if (name) {
      if (id) m.set(id, name);
      if (userId && userId !== id) m.set(userId, name);
    }
  }
  for (const u of tenantUsers?.results ?? []) {
    const id = u.id?.trim();
    if (!id) {
      continue;
    }
    const name =
      [u.first_name, u.last_name].filter(Boolean).join(" ").trim() ||
      u.email ||
      u.username;
    if (name) {
      m.set(id, name);
    }
  }
  return m;
}

/** Resolve UUID requester / assignee on Kanban (and similar) rows — same IDs as agent/tenant user lists. */
export function enrichAdminTicketsWithUserMap(
  tickets: AdminTicket[],
  map: Map<string, string>
): AdminTicket[] {
  if (map.size === 0) {
    return tickets;
  }
  return tickets.map(t => {
    let requester = t.requester;
    if (requester && looksLikeOpaqueUserId(requester)) {
      requester = getUserDisplayFromMap(map, requester) ?? requester;
    }
    let assignedAgent = t.assignedAgent;
    if (assignedAgent && looksLikeOpaqueUserId(assignedAgent)) {
      assignedAgent = getUserDisplayFromMap(map, assignedAgent) ?? assignedAgent;
    }
    if (requester === t.requester && assignedAgent === t.assignedAgent) {
      return t;
    }
    return { ...t, requester, assignedAgent };
  });
}

/** Assignee dropdown options from agent/employee list API bodies. */
export function agentAssigneeOptionsFromPayload(
  payload: unknown
): { id: string; name: string }[] {
  const out: { id: string; name: string }[] = [];
  for (const raw of extractPayloadArray(payload)) {
    if (!raw || typeof raw !== "object") {
      continue;
    }
    const o = raw as Record<string, unknown>;
    const id = String(o.id ?? o.user_id ?? o.uuid ?? "").trim();
    // Support both snake_case (first_name) and camelCase-less (firstname) field names
    const fullName = [o.first_name ?? o.firstname, o.last_name ?? o.lastname]
      .filter(Boolean)
      .map(String)
      .join(" ")
      .trim();
    const name = firstNonEmptyString(
      o.name,
      o.full_name,
      o.display_name,
      fullName,
      o.emailaddress,
      o.email,
      o.username
    );
    if (id && name) {
      out.push({ id, name });
    }
  }
  return out.sort((a, b) => a.name.localeCompare(b.name));
}
